import React, { useState, useEffect } from "react";
import { Card, Form, Button, Spinner } from "react-bootstrap";
import * as XLSX from "xlsx";
import {API_BASE} from "../services/apirest";

export default function ImportarSolicitudes() {
    const [file, setFile] = useState(null);
    const [destino, setDestino] = useState("solicitudes"); // 'solicitudes' | 'black_list'
    const [mode, setMode] = useState("upsert");            // insert | upsert
    const [delimiter, setDelimiter] = useState(",");       // , o ;
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    // Opcionales:
    const [honorNo, setHonorNo] = useState(false); // usar "No." como id en black_list
    const [firstLoadVisto, setFirstLoadVisto] = useState(false); // marcar Estado="Visto"

    useEffect(() => {
        setMode(destino === "black_list" ? "insert" : "upsert");
    }, [destino]);

    async function parseXlsxToCsvBlob(xlsxFile, sep) {
        const buf = await xlsxFile.arrayBuffer();
        const wb = XLSX.read(buf);
        const preferredSheet =
            destino === "solicitudes" && wb.SheetNames.includes("PLANTILLA")
                ? "PLANTILLA"
                : wb.SheetNames[0];
        const ws = wb.Sheets[preferredSheet];
        const csvText = XLSX.utils.sheet_to_csv(ws, { FS: sep || "," });
        const blob = new Blob([csvText], { type: "text/csv" });
        return new File([blob], "import_from_excel.csv", { type: "text/csv" });
    }

    function pickEndpoint() {
        return destino === "solicitudes"
            ? `${API_BASE}/api/solicitudes_import.php`
            : `${API_BASE}/api/black_list_import.php`;
    }

    async function handleUpload(e) {
        e.preventDefault();
        if (!file) return;

        setLoading(true);
        setResult(null);

        try {
            let fileForUpload = file;
            if (/\.(xlsx|xls)$/i.test(file.name)) {
                fileForUpload = await parseXlsxToCsvBlob(file, delimiter);
            }

            const form = new FormData();
            form.append("file", fileForUpload);
            form.append("mode", mode);
            form.append("delimiter", delimiter);

            if (destino === "black_list" && honorNo) form.append("honor_no", "1");
            if (destino === "solicitudes" && firstLoadVisto) form.append("first_load", "1");

            // Lee el token del localStorage. (Quita comillas por si se guardó con JSON.stringify)
            let token = localStorage.getItem("token") || "";
            token = token.replace(/^"+|"+$/g, "");

            // Máxima compatibilidad: pásalo en header, en form y en querystring
            if (token) form.append("token", token);

            let endpoint = pickEndpoint();
            if (token) {
                const sep = endpoint.includes("?") ? "&" : "?";
                endpoint += `${sep}token=${encodeURIComponent(token)}`;
            }

            const headers = {};
            if (token) headers["Authorization"] = `Bearer ${token}`;

            const res = await fetch(endpoint, {
                method: "POST",
                headers, // no pongas Content-Type manual
                body: form,
            });

            // Lee respuesta segura
            const text = await res.text();
            let data;
            try { data = JSON.parse(text); } catch { data = { error: `HTTP ${res.status}`, raw: text }; }

            if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);

            setResult(data);
        } catch (err) {
            setResult({ error: String(err) });
        } finally {
            setLoading(false);
        }
    }


    return (
        <Card className="p-3">
            <h5 className="mb-3">Importar datos</h5>
            <Form onSubmit={handleUpload} className="d-grid gap-3">
                <Form.Group>
                    <Form.Label>Destino</Form.Label>
                    <Form.Select
                        value={destino}
                        onChange={(e) => setDestino(e.target.value)}
                    >
                        <option value="solicitudes">Tabla: solicitudes_usuarios</option>
                        <option value="black_list">Tabla: black_list</option>
                    </Form.Select>
                    <Form.Text className="text-muted">
                        • <b>solicitudes_usuarios</b>: /api/solicitudes_import.php
                        <br />
                        • <b>black_list</b>: /api/black_list_import.php
                    </Form.Text>
                </Form.Group>

                <Form.Group>
                    <Form.Label>Archivo (.csv o .xlsx)</Form.Label>
                    <Form.Control
                        type="file"
                        accept=".csv,.xlsx,.xls"
                        onChange={(e) => setFile(e.target.files?.[0] || null)}
                    />
                </Form.Group>

                <div className="d-flex gap-3 flex-wrap">
                    <Form.Group>
                        <Form.Label>Modo</Form.Label>
                        <Form.Select
                            value={mode}
                            onChange={(e) => setMode(e.target.value)}
                        >
                            <option value="insert">Solo insertar</option>
                            <option value="upsert">Insertar/Actualizar (índice único)</option>
                        </Form.Select>
                        <Form.Text className="text-muted">
                            Sugerido: <b>insert</b> para <code>black_list</code>,{" "}
                            <b>upsert</b> para <code>solicitudes</code>.
                        </Form.Text>
                    </Form.Group>

                    <Form.Group>
                        <Form.Label>Separador CSV</Form.Label>
                        <Form.Select
                            value={delimiter}
                            onChange={(e) => setDelimiter(e.target.value)}
                        >
                            <option value=",">Coma ,</option>
                            <option value=";">Punto y coma ;</option>
                        </Form.Select>
                    </Form.Group>
                </div>

                {destino === "black_list" && (
                    <Form.Check
                        type="checkbox"
                        id="honorNo"
                        label='Usar la columna "No." del Excel como ID (sólo cargas históricas)'
                        checked={honorNo}
                        onChange={(e) => setHonorNo(e.target.checked)}
                    />
                )}

                {destino === "solicitudes" && (
                    <Form.Check
                        type="checkbox"
                        id="firstLoadVisto"
                        label='Primera carga: marcar Estado = "Visto"'
                        checked={firstLoadVisto}
                        onChange={(e) => setFirstLoadVisto(e.target.checked)}
                    />
                )}
                localStorage.getItem('token')

                <Button type="submit" disabled={!file || loading}>
                    {loading ? <Spinner size="sm" /> : "Importar"}
                </Button>
            </Form>

            {result && (
                <pre className="mt-3 small bg-light p-2 rounded">
{JSON.stringify(result, null, 2)}
        </pre>
            )}
        </Card>
    );
}
